import { Device, TrapType, CollectionSite } from "./index";
import type { Dayjs } from "dayjs";

export interface CollectionsFormData {
  selectedDevice?: Device[];
  startingDate?: Dayjs | null;
  endingDate?: Dayjs | null;
  trapTypes?: TrapType[];
  collectionSites?: CollectionSite[];
  page?: number;
  pageSize?: number;
}

/**
 * A collection of specimens grouped together
 * @export
 * @interface Collection
 */
export interface Collection {
  /**
   * unique id to identify this collection
   * @type {string}
   * @memberof Collection
   */
  readonly _id?: string;
  /**
   * user defined name
   * @type {string}
   * @memberof Collection
   */
  readonly id: string;
  /**
   * user defined name
   * @type {string}
   * @memberof Collection
   */
  displayName?: string;
  /**
   * unique id to identify the device that is capturing this collection
   * @type {string}
   * @memberof Collection
   */
  deviceUuid: string;
  /**
   * the utc time when this collection was created
   * @type {string}
   * @memberof Collection
   */
  createdAt: string;
  /**
   * user description of this collection
   * @type {string}
   * @memberof Collection
   */
  description?: string;
  /**
   * user defined id
   * @type {string}
   * @memberof Collection
   */
  trapId?: string;
  /**
   * unique id to identify the collectionSite
   * @type {string}
   * @memberof Collection
   */
  collectionSiteId?: string;
  /**
   * unique id to identify the trapType
   * @type {string}
   * @memberof Collection
   */
  trapTypeId?: string;
  /**
   * the utc date the specimens in this collection were collected
   * @type {string}
   * @memberof Collection
   */
  collectionDate?: string;
  /**
   *
   * @memberof Collection
   */
  runs?: Run[];
}

export enum RunStatusEnum {
  NUMBER_0 = 0,
  NUMBER_1 = 1,
  NUMBER_2 = 2,
  NUMBER_3 = 3,
  NUMBER_4 = 4,
  NUMBER_5 = 5,
  NUMBER_6 = 6,
}

export const getStatusInString = (status: number) => {
  switch (status) {
    case 0:
      return "Created";
    case 1:
      return "Capturing";
    case 2:
      return "Paused";
    case 3:
      return "Finishing";
    case 4:
      return "Finished";
    case 5:
      return "Errored";
    case 6:
      return "Deleted";
    default:
      return "Unknown";
  }
};

export interface Run {
  /**
   * numeric id to identify this run within this collection
   * @type {number}
   * @memberof Run
   */
  readonly runId: number;
  /**
   *
   * @type {RunStatusEnum}
   * @memberof Run
   */
  readonly status: RunStatusEnum;
  /**
   * the utc time when this collection was started
   * @type {string}
   * @memberof Run
   */
  startTime: string;
  /**
   * the utc time when this collection was stopped
   * @type {string}
   * @memberof Run
   */
  readonly endTime?: string;
}

export const parseCollectionFormDataToParams = (
  filters: CollectionsFormData
) => {
  let parameter = [];
  if (filters.startingDate) {
    parameter.push({
      key: "dateFrom",
      value: filters.startingDate.format("YYYY-MM-DD"),
    });
  }
  if (filters.endingDate) {
    parameter.push({
      key: "dateTo",
      value: filters.endingDate.format("YYYY-MM-DD"),
    });
  }

  if (filters.selectedDevice) {
    if (filters.selectedDevice.length > 0) {
      filters.selectedDevice.forEach((value, i) => {
        parameter.push({
          key: "deviceIds",
          value: value.id,
        });
      });
    }
  }
  if (filters.collectionSites) {
    if (filters.collectionSites.length > 0) {
      filters.collectionSites.forEach((value) => {
        parameter.push({
          key: "collectionSiteIds",
          value: value.id,
        });
      });
    }
  }
  if (filters.trapTypes) {
    if (filters.trapTypes.length > 0) {
      filters.trapTypes.forEach((value) => {
        parameter.push({
          key: "trapTypeIds",
          value: value.id,
        });
      });
    }
  }
  if (filters.page || filters.page === 0) {
    parameter.push({
      key: "page",
      value: filters.page,
    });
  }

  if (filters.pageSize) {
    parameter.push({
      key: "pageSize",
      value: filters.pageSize,
    });
  }

  if (parameter.length === 0) {
    return "";
  }

  return parameter
    .map(({ key, value }) => {
      return `${key}=${value}`;
    })
    .join("&");
};

export interface CollectionFormData {
  collectionSiteId?: CollectionSite | null;
  trapTypeId?: TrapType | null;
  collectionDate: Dayjs | null;
  description?: string | null;
  id?: string;
}

export interface CollectionSpecimenSummary {
  count: number;
  male: number;
  female: number;
  unsure: number;
  displayName: string;
}

export interface CollectionsSummary extends Collection {
  data: CollectionSpecimenSummary[];
  [key: string]: any;
}
